    import { hrmsGet, hrmsPost, hrmsPut } from "../api/api";
    import sessionDB from "../utils/sessionDB";

    /**
     * Shift service — organization-shifts module.
     * Endpoints per Swagger (`/saas/hrms/emp_shift/*`):
     *   GET    /saas/hrms/emp_shift/search                Get all shifts (paginated)
     *   GET    /saas/hrms/emp_shift/{shiftId}              Get shift by id
     *   POST   /saas/hrms/emp_shift/add                    Create shift
     *   PUT    /saas/hrms/emp_shift/update/{shiftId}        Update shift
     *   DELETE /saas/hrms/emp_shift/delete/{shiftId}        Delete shift
     * Response envelope: { message, data, timestamp, encrypted, key }
     */

    /**
     * "HH:MM" -> full ISO datetime string the API expects (e.g. "2026-07-16T09:00:00.000Z").
     * Already-ISO values (e.g. when a server-shaped shift object is round-tripped,
     * such as toggling isActive from the list page) are passed through untouched.
     */
    function toIsoDateTime(value) {
      if (!value) return null;
      if (value.includes("T")) return value;
      const [h, m] = value.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      const d = new Date();
      d.setHours(h, m, 0, 0);

      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    }

    /** "" -> 0, otherwise Number(value). Backend numeric fields appear to be primitive ints that reject null. */
    function toNumberOrZero(value) {
      if (value === "" || value === null || value === undefined) return 0;
      const n = Number(value);
      return Number.isNaN(n) ? 0 : n;
    }

    /**
     * Maps the internal form state (used by the UI/validation hooks) to the
     * exact request-body shape required by the Swagger schema for
     * POST /emp_shift/add and PUT /emp_shift/update/{shiftId}.
     */
    export function mapFormStateToApiPayload(formState) {
      return {
        shiftName: formState.shiftName?.trim() ?? "",
        tenantId: toNumberOrZero(sessionDB.getTenantId()),
        shiftStartTime: toIsoDateTime(formState.shiftStartTime),
        shiftEndTime: toIsoDateTime(formState.shiftEndTime),
        isActive: !!formState.isActive,
        strictWorkingHours: toNumberOrZero(formState.strictWorkingHours),
        halfDayWorkingHours: toNumberOrZero(formState.halfDayWorkingHours),
        fullDayWorkingHours: toNumberOrZero(formState.fullDayWorkingHours),
        lateArrivalBufferMinutes: toNumberOrZero(formState.lateArrivalBufferMinutes),
        earlyExitBufferMinutes: toNumberOrZero(formState.earlyExitBufferMinutes),
        overtimeEnabled: !!formState.overtimeEnabled,
        minimumOvertimeHours: formState.overtimeEnabled
          ? toNumberOrZero(formState.minimumOvertimeHours)
          : null,
        halfDayOvertimeHours: formState.overtimeEnabled
          ? toNumberOrZero(formState.halfDayOvertimeHours)
          : null,
        fullDayOvertimeHours: formState.overtimeEnabled
          ? toNumberOrZero(formState.fullDayOvertimeHours)
          : null,
        compOffEnabled: !!formState.compOffEnabled,
        hrCanMarkAttendance: !!formState.hrCanMarkAttendance,
        hrCanAmendTime: !!formState.hrCanAmendTime,
        mandatoryReportBeforeCheckout: !!formState.mandatoryReportBeforeCheckout,
        regularizationApprovalFlow: formState.regularizationApprovalFlow,
      };
    }

    function unwrapList(response) {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response?.data?.content)) return response.data.content;
      if (Array.isArray(response?.content)) return response.content;
      return [];
    }

    function unwrapItem(response) {
      if (response?.data && typeof response.data === "object") return response.data;
      return response;
    }

    /** GET /saas/hrms/emp_shift/search?page=0&size=1000&sortBy=shiftName&sortDir=ASC */
    export async function fetchAllShifts() {
      try {
        const response = await hrmsGet(
          "/emp_shift/search?page=0&size=10&sortBy=shiftName&sortDir=ASC"
        ,"HR19A");
        const list = unwrapList(response);
        return list.map((s) => ({ ...s, id: s.shiftId }));
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /** GET /saas/hrms/emp_shift/{shiftId} */
    export async function fetchShiftById(id) {
      try {
        const response = await hrmsGet(`/emp_shift/${id}`,"HR19A");
        const item = unwrapItem(response);
            return item ? { ...item, id: item.shiftId } : item;

      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /** POST /saas/hrms/emp_shift/add */
    export async function createShiftOnServer(formState) {
      try {
        const body = mapFormStateToApiPayload(formState);
        const response = await hrmsPost("/emp_shift/add", body,"HR24A");
        return unwrapItem(response);
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /** PUT /saas/hrms/emp_shift/update/{shiftId} */
    export async function updateShiftOnServer(formState) {
      try {
        const body = mapFormStateToApiPayload(formState);
        const response = await hrmsPut(`/emp_shift/update/${formState.id}`, body,"HR24U");
        return unwrapItem(response);
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /** DELETE /saas/hrms/emp_shift/delete/{shiftId} */


    /** GET /saas/hrms/emp_shift/count/active */
    /** GET /saas/hrms/emp_shift/count/active */
    export async function fetchActiveShiftCount() {
      try {
        const response = await hrmsGet("/emp_shift/count/active", "HR19A");
        if (typeof response === "number") return response;
        if (typeof response?.data === "number") return response.data;
        const data = unwrapItem(response);
        return typeof data === "number" ? data : data?.count ?? 0;
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /** GET /saas/hrms/emp_shift/active */
    export async function fetchActiveShifts() {
      try {
        const response = await hrmsGet("/emp_shift/active","HR19A");
        const list = unwrapList(response);
        return list.map((s) => ({ ...s, id: s.shiftId }));
      } catch (error) {
        console.error(error);
        throw error;
      }
    }


    /** GET /saas/hrms/emp_shift/search/overtime?enabled=true|false */
    export async function fetchShiftsByOvertime(enabled) {
      try {
        const response = await hrmsGet(`/emp_shift/search/overtime?enabled=${enabled}`,"HR19A");
        return unwrapList(response);
      } catch (error) { console.error(error); throw error; }
    }

    /** GET /saas/hrms/emp_shift/search/comp-off?enabled=true|false */
    export async function fetchShiftsByCompOff(enabled) {
      try {
        const response = await hrmsGet(`/emp_shift/search/comp-off?enabled=${enabled}`,"HR19A");
        return unwrapList(response);
      } catch (error) { console.error(error); throw error; }
    }

    /** GET /saas/hrms/emp_shift/search/approval-flow?flow=Manager|HR|HR_Then_Manager */
    export async function fetchShiftsByApprovalFlow(flow) {
      try {
        const response = await hrmsGet(`/emp_shift/search/approval-flow?flow=${encodeURIComponent(flow)}`,"HR19A");
        return unwrapList(response);
      } catch (error) { console.error(error); throw error; }
    }

    /**
     * Checks how many employees are assigned to a shift before allowing deletion.
     * See OPEN ITEM note below — reads `assignedEmployeeCount` off the shift-detail
     * payload until a real assignment-check endpoint is confirmed with the backend.
     */
    export async function checkShiftAssignment(id) {
      try {
        const shift = await fetchShiftById(id);
        return { assignedEmployeeCount: shift?.assignedEmployeeCount ?? 0 };
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    /**
     * NOTE / OPEN ITEM: no dedicated "check assignment" endpoint exists in the
     * Swagger list provided (only search/{shiftId}/add/update/delete/count/active
     * variants are documented). ShiftsListPage calls this BEFORE opening the


     * Flag this to the backend team — swap the implementation once a real
     * assignment-check endpoint (e.g. /saas/hrms/emp_shift/{shiftId}/assignments)
     * is available.
     */